import { MouseEvent } from "react";
import { TableDataField, TableHeader } from "../components/Table/DataTable";
import { allMethods } from "./supplementalData";
import { customInstance } from "../api/custom-instance";
import { SingleClassificationModel } from "../types/models.types";

export function capitalize(text:string) {
    return text.replace(/\b\w/g , function(m){ return m.toUpperCase(); } );
};

export function splitLabel(text: string) {
    return text.split('_').map(word => capitalize(word)).join(' ');
}

export function formatDateString(dateString: string) {
    return new Date(dateString).toLocaleString('en-US', {timeZone: 'EST'})
}

export const convertNumberToDecimal = (value: number | string) => {
    if (isNaN(Number(value))) {
        return value.toString();
    } else {
        const numValue = Number(value);
        if ( numValue % 1 !== 0 ) {
            return (Math.round(numValue * 100) / 100 as number).toFixed(2);
        } else {
            return value.toString();
        }
    }
}

export const convertNumberToPercentage = (min: number, max: number, decimal: number) => {
    const difference = Math.abs(max - min);
    const ratio = difference * decimal;
    const mark = min + ratio;
    return mark;
};


export const formatBytes = (bytes:number) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}


export const generateRandomString = () => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < 10; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

export function getRandomColor() {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

export const getDatedFilename = (prefix: string) => {
    const now = new Date();
    return prefix + '-' + now.toISOString().substring(0, 19) + '.csv';
}

export const getFileExt = (ext: string) => {
    if (ext === 'text/csv') {
        return 'csv';
    } else if (ext === 'chemical/x-mdl-sdf') {
        return 'sdf';
    } else {
        return 'xlsx';
    }
}

export const getStageColor = (value: string) => {
    const status = value.toString().toLocaleLowerCase();

    let label = '';
    let background = '';

    switch(status) {
        case 'changed':
            //yellow
            label = '#8F8319';
            background = 'rgba(255, 242, 120, 0.4)';
            break;
        case 'debug':
            //yellow
            label = '#8F8319';
            background = 'rgba(255, 242, 120, 0.4)';
            break;
        case 'queued':
            //yellow
            label = '#8F8319';
            background = 'rgba(255, 242, 120, 0.4)';
            break;
        case 'removed': 
            //orange
            label = '#47250D';
            background = 'rgba(255, 164, 97, 0.4)';
            break;
        case 'warning': 
            //orange
            label = '#47250D';
            background = 'rgba(255, 164, 97, 0.4)';
            break;
        case 'running': 
            //orange
            label = '#47250D';
            background = 'rgba(255, 164, 97, 0.4)';
            break;
        case 'error': 
            //red
            label = '#8F1F19';
            background = 'rgba(255, 132, 132, 0.4)';
            break;
        case 'failed': 
            //red
            label = '#8F1F19';
            background = 'rgba(255, 132, 132, 0.4)';
            break;
        case 'fail': 
            //red
            label = '#8F1F19';
            background = 'rgba(255, 132, 132, 0.4)';
            break;
        case 'info':
            //blue
            label = '#003477';
            background = 'rgba(121, 191, 201, 0.4)';
            break;
        case 'success':
            label='#3C7828';
            background = 'rgba(199, 219, 120, 0.4)';
    }

    return {
        label,
        background,
    }
}

// const highest = '#7BC960';
// const high = '#C7DB78';
// const medium = '#FFF278';
// const low = '#FFA461';
// const lowest = '#FF8484';

// const active = '#7BC960';
// const inactive = '#FF8484';
// const inconclusive = '#FFA461';
// const outOfDomain = '#E0E0E0';

export const statColors = {
    highestColor: '#9CD987',
    highColor: '#D4E590',
    mediumColor: '#FFF69F',
    lowColor: '#FBBF93',
    lowestColor:'#F89A9A',
    neutralColor: '#E0E0E0',
}

export const conformalStatColors = {
    activeColor: '#9CD987',
    inactiveColor: '#F89A9A',
    inconclusiveColor: '#FBBF93',
    outOfDomainColor: '#e0e0e0',
}

export const normalizeConformalMethodLabel = (label: string) => {
    return label.replace(/\s*\(DCM\)\s*$/i, '').trim();
}

export const isConformalMethodLabel = (label: string) => {
    return allMethods.includes(normalizeConformalMethodLabel(label));
}

export const getStatsCellColor = (limits: { min: number, max: number }, value: number, isReversed: boolean) => {
    const { min, max } = limits;

    const { 
        highestColor,
        highColor,
        mediumColor,
        lowColor,
        lowestColor,
        neutralColor
    } = statColors;

    if (value <= convertNumberToPercentage(min, max, 0.3)) {
        if (isReversed) {
            return highestColor;
        } else {
            return lowestColor;
        }
    } else if (value > convertNumberToPercentage(min, max, 0.3) && value <= convertNumberToPercentage(min, max, 0.49) ) {
        if (isReversed) {
            return highColor;
        } else {
            return lowColor
        }
    } else if (value > convertNumberToPercentage(min, max, 0.49) && value <= convertNumberToPercentage(min, max, 0.69)) {
        return mediumColor;
    } else if (value > convertNumberToPercentage(min, max, 0.69) && value <= convertNumberToPercentage(min, max, 0.89)) {
        if (isReversed) {
            return lowColor;
        } else {
            return highColor;
        }
    } else if (value > convertNumberToPercentage(min, max, 0.89)) {
        if (isReversed) {
            return lowestColor;
        } else {
            return highestColor;
        }
    } else {
        return neutralColor;
    }
};

const getConformalModeColor = (modeLabel: string)  => {
    const {
        outOfDomainColor,
        inconclusiveColor,
        inactiveColor,
        activeColor,
    } = conformalStatColors;

    if (modeLabel === 'Out of Domain') {
        return { color: outOfDomainColor, label: 'Out of Domain' }
    } else if (modeLabel === 'Inconclusive') {
        return { color: inconclusiveColor, label: 'Inconclusive' }
    } else if (modeLabel === 'Inactive') {
        return { color: inactiveColor, label: 'Inactive' }
    } else {
        return { color: activeColor, label: 'Active' }
    }
}

export const calculateConformalMode = (data: TableDataField[], headers: TableHeader[], threshold: number) => {
    let arr: string[] = [];
    let frequency: {[key:string]: number} = {};

    data.forEach(field => {
        const matchingHeader = headers.find(header => header.value === field.associatedColumn);
        if (matchingHeader && isConformalMethodLabel(matchingHeader.label)) {
            const value = field.value as number[];
            const { label } = getConformalValue(value, threshold);
            arr.push(label);
        }
    });

    arr.forEach(field => {
        const label = Object.keys(frequency).find(key => key === field);
        if (!label) {
            frequency[field as keyof object] = 1;
        } else {
            frequency[label as keyof object] = frequency[label as keyof object]+1;
        }
    })


    let maxValue = 0;
    let maxKey = null;
    let duplicates: string[] = [];

    for (const [key, value] of Object.entries(frequency)) {
        if (value > maxValue) {
            maxValue = value;
            maxKey = key;
        }
    }

    for (const [key, value] of Object.entries(frequency)) {
        if (value === maxValue) {
            duplicates.push(key);
        }
    };

    const {
        outOfDomainColor,
        inconclusiveColor,
        inactiveColor,
        activeColor,
    } = conformalStatColors;

    if (maxKey && duplicates.length <= 1) {
        return getConformalModeColor(maxKey);
    } else if (maxKey && duplicates.length > 1) {
        if (duplicates.includes('Out of Domain')) {
            return { color: outOfDomainColor, label: 'Out of Domain' }
        } else if (duplicates.includes('Inconclusive')) {
            return { color: inconclusiveColor, label: 'Inconclusive' }
        } else if (duplicates.includes('Inactive')) {
            return { color: inactiveColor, label: 'Inactive' }
        } else {
            return { color: activeColor, label: 'Active' }
        }
    } else {
        return { color: outOfDomainColor, label: 'Out of Domain' }
    }

}


export const getConformalValue = (numArr: number[], threshold: number) => {
    if (numArr === undefined) {
        return { color: '#ffffff', label: 'N/A' }
    }
    const inactive = numArr[0];
    const active = numArr[1];

    const {
        outOfDomainColor,
        inconclusiveColor,
        inactiveColor,
        activeColor,
    } = conformalStatColors;

    if (inactive < threshold && active < threshold) {
        return { color: outOfDomainColor, label: 'Out of Domain' }
    } else if (inactive > threshold && active > threshold) {
        return { color: inconclusiveColor, label: 'Inconclusive' }
    } else if (inactive > threshold && active < threshold) {
        return { color: inactiveColor, label: 'Inactive' }
    } else {
        return { color: activeColor, label: 'Active' }
    }
}

export const exportData = async (
    items: { name: string, hash: string }[], 
    file_type: string, 
    data_type: 'datafile' | 'dataset' | 'resultset' | 'visualization' | 'model', 
    rsId?: number
) => {
    const promises = items.map(async (item) => {
        const { hash, name } = item;
        let url = '';

        if (data_type === 'datafile') {
            url = `data_file/download/${hash}?file_type=${file_type}`;
        } else if (data_type === 'dataset') {
            url = `dataset/download/${hash}?file_type=${file_type}`;
        } else if (data_type === 'resultset') {
            url = `resultset/download/${hash}?file_type=${file_type}&applicability_domain=true&include_duplicates=true&include_duplicate_values=true`;
        } else if (data_type === 'visualization' && rsId !== undefined) {
            url = `resultset/dataset_visualization_download/${rsId}/${hash}?file_type=${file_type}`;
        } else if (data_type === 'model') {
            url = `model/download/${hash}`;
        };

        return customInstance({
            url,
            responseType: 'blob',
            method: 'GET',
        }).then(response => {downloadData(name, file_type, response)});
    });

    try {
        await Promise.all(promises);
    } catch (err:any) {
        console.log(err);
    }
};


export const downloadData = (name: string, file_type: string, data: any) => {
    let opt;
    if (file_type === '.xlsx') opt = { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' };
    else if (file_type === '.csv') opt = { type: 'text/csv' };
    else if (file_type === '.sdf') opt = { type: 'chemical/x-mdl-sdfile' };
    else if (file_type === '.parquet') opt = { type: 'application/vnd.apache.parquet' };
    else if (file_type === '.zip') opt = { type: 'application/zip' };
    else opt = { type: 'text/plain' };
  
    let blob = new Blob([data], opt); // pass a useful mime type here
    let url = URL.createObjectURL(blob);

    const filename = `${name}${file_type}`;
  
    let element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', filename);
  
    element.style.display = 'none';
    document.body.appendChild(element);
  
    element.click();
  
    document.body.removeChild(element);
  }


export const downloadStructureImage = (e: MouseEvent<HTMLButtonElement>, identifier: string, structureId?: string) => {
    e.preventDefault();
    const svgContainer = document.getElementById(structureId ? structureId : 'molecule-structure') as SVGElement | null;
    if (!svgContainer) return;
    
    const svgElement = svgContainer.querySelector('svg');
    if (!svgElement) return;
    
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext("2d");

    if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Convert to PNG and trigger download
        canvas.toBlob((blob) => {
        if (blob) {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${identifier}.png`;
            link.click();
            URL.revokeObjectURL(link.href);
        }
        }, "image/png");
    }
    };

    img.src = url;
  
    
  };


  export function moveItemInArray(arr: any[], oldIndex: number, newIndex: number) {
    // Handle negative indices to ensure they are within the array bounds
    while (oldIndex < 0) {
      oldIndex += arr.length;
    }
    while (newIndex < 0) {
      newIndex += arr.length;
    }
  
    // If the new index is out of bounds, pad the array with undefined
    if (newIndex >= arr.length) {
      let k = newIndex - arr.length;
      while ((k--) + 1) {
        arr.push(undefined);
      }
    }
  
    // Remove the item from its old position and store it
    const [movedItem] = arr.splice(oldIndex, 1);
  
    // Insert the stored item at the new position
    arr.splice(newIndex, 0, movedItem);
  
    return arr;
  }
  

  export const model_overlay_data = (yTrue?: number[], yPred?: number[], yProb?: number[]) => {
    if (yTrue === undefined || yPred === undefined || yProb === undefined) {

    return null;

    } else {

    let pred_vals = [];
    let true_vals = [];
    let i = 0;
    for (let val of yTrue) {
        if (val === 0) {
        pred_vals.push(yProb[i])
        }
        i++;
    }
    let j = 0;
    for (let val of yTrue) {
        if (val === 1) {
        true_vals.push(yProb[j])
        }
        j++;
    }

    var trace1 = {
        x: pred_vals,
        name: 'Predicted Values',
        autobinx: false, 
        histnorm: "count", 
        marker: {
        color: "rgb(219, 30, 58, .7)", 
        line: {
            color:  "rgb(219, 30, 58, 1)", 
            width: 1
        }
        },  
        opacity: 0.5, 
        type: "histogram", 
        xbins: {
        end: 1, 
        size: 0.025, 
        start: 0
        }
        };

    var trace2 = {
        x: true_vals,
        name: "True Values", 
        histnorm: "count",
        autobinx: false, 
        marker: {
                color: "rgb(94, 146, 219, .7)",
                line: {
                color:  "rgb(94, 146, 219, 1)", 
                width: 1
        } 
            }, 
        opacity: 0.5, 
        type: "histogram", 
        xbins: { 
        end: 1, 
        size: 0.025, 
        start: 0
        }
        };

    return [trace1, trace2]

    }
    
}

export const modelOverlayLayout = {
    barmode: "overlay", 
    title: {
        text: "Model Evaluation Overlay",
        yref: 'paper',
        xref: 'paper',
        yanchor: 'bottom',
        y: 1,
        pad: {
        b:10,
        l:0,
        r:0,
        t:10
        },
        xanchor: 'auto',
        font: {
        family: 'Montserrat',
        size: 16,
        }
    },
    responsive: true,
    autosize: true, 
    showlegend:false,
    margin: {
        l: 38,
        r: 10,
        b: 38,
        t: 60,
        pad: 0
    },
    modebar: {
        color: '#555555',
        activecolor: '#315FB5',
    },
    xaxis: {
        title: {
            text: 'Value',
            font: {
                family: 'Montserrat',
                size: 14,
            },
            
        }
    }, 
    yaxis: {
        title: {
            text: 'Count',
            font: {
                family: 'Montserrat',
                size: 14,
            },
        }
    }, 
    }

export const rocLayout = {
    title: {
        text: '5-fold Cross Validation ROC',
        yref: 'paper',
        xref: 'paper',
        yanchor: 'bottom',
        y: 1,
        pad: {
        b:10,
        l:0,
        r:0,
        t:10
        },
        xanchor: 'auto',
        font: {
        family: 'Montserrat',
        size: 16,
        }
    },
    responsive: true,
    autosize: true,  
    showlegend:false,
    margin: {
        l: 30,
        r: 10,
        b: 30,
        t: 60,
        pad: 0
    },
    modebar: {
        color: '#555555',
        activecolor: '#315FB5',
    },
    shapes: [
        {
        type: 'line',
        x0: 0,
        y0: 0,
        x1: 1,
        y1: 1,
        line: {
            color: '#999999',
            width: 2,
            dash: 'dot'
        }
        }
    ]

}

export const truth_table_data = (tp?: number, fp?: number, fn?: number, tn?: number) => {
    if (tp === undefined || fp === undefined || fn === undefined || tn === undefined) {
        return null
    } else {
        return [
            {
                name: 'True Positive',
                value: tp,
            },
            {
                name: 'False Positive',
                value: fp,
            },
            {
                name: 'False Negative',
                value: fn,
            },
            {
                name: 'True Negative',
                value: tn,
            }
        ]
    }
};

export const formatTruthTableData = (data: {name: string, value: number}[]) => {
    let traces: Plotly.Data[] = [];
    let tableLayout: Partial<Plotly.Layout> | null = null;
    if (data.length > 0) {
        let TP = data.find(d => d.name === 'True Positive');
        let TN = data.find(d => d.name === 'True Negative');
        let FP = data.find(d => d.name === 'False Positive');
        let FN = data.find(d => d.name === 'False Negative');
        if (TP !== undefined && TN !== undefined && FP !== undefined && FN !== undefined) {
            const total = data.reduce(
                (acc, value) => value.value + acc, 0
            )

            const totalTruths = TP.value + TN.value;
            const totalFalses = FP.value + FN.value;

            const zBlue = [
                [null, null],
                [TP.value, TN.value]
            ]
            
            const zRed = [
                [FP.value, FN.value],
                [null, null]
            ]

            traces = [
                {
                    z: zRed,
                    x: ['Positive', 'Negative'],
                    y: ['False', 'True'],
                    type: 'heatmap',
                    colorscale: [
                        [0, '#FFFFFF'],
                        [1, '#F47948'],
                    ],
                    zmin: 0,
                    zmax: total,
                    colorbar: {
                        tickvals: [],
                        ticktext: [],
                        thickness: 18,
                        x: 1.02,  // Adjust position to the right
                    },
                    hoverinfo: 'none',
                    
                },
                {
                    z: zBlue,
                    x: ['Positive', 'Negative'],
                    y: ['False', 'True'],
                    type: 'heatmap',
                    colorscale: [
                        [0, '#FFFFFF'],
                        [1, '#5385BA'],
                    ],
                    zmin: 0,
                    zmax: total,
                    colorbar: {
                        tickvals: [0, total],
                        ticktext: ['0%','100%'],
                        thickness: 18,
                        x: 1.09,  // Adjust position to the right
                    },
                    hoverinfo: 'none',
                }
            ]
            

            

            tableLayout = {
                title: {
                    text: 'Model Truth Table',
                    font: {
                        family: 'Montserrat',
                        size: 16,
                    },
                    y: 0.9, // Adjust this value to move the title closer or further away from the heatmap
                    x: 0.5,  // Center the title horizontally
                    xanchor: 'center',
                    yanchor: 'top', // Anchor the title to the top
                },
                autosize: true,
                xaxis: {
                    tickvals: [0, 1],
                    ticktext: ['Positive', 'Negative'],
                },
                yaxis: {
                    tickvals: [0, 1],
                    ticktext: [
                        `False (${Math.round((totalFalses/total)*100)}%)`, 
                        `True (${Math.round((totalTruths/total)*100)}%)`, 
                    ],
                    automargin: true,
                },
                annotations: [
                    {
                        x: 0,
                        y: 0,
                        text: `${FP.value.toString()} (${Math.round((FP.value/total)*100)}%)`,
                        showarrow: false,
                        font: { size: 14 },
                    },
                    {
                        x: 1,
                        y: 0,
                        text: `${FN.value.toString()} (${Math.round((FN.value/total)*100)}%)`,
                        showarrow: false,
                        font: { size: 14 },
                    },
                    {
                        x: 0,
                        y: 1,
                        text: `${TP.value.toString()} (${Math.round((TP.value/total)*100)}%)`,
                        showarrow: false,
                        font: { size: 14 },
                    },
                    {
                        x: 1,
                        y: 1,
                        text: `${TN.value.toString()} (${Math.round((TN.value/total)*100)}%)`,
                        showarrow: false,
                        font: { size: 14 },
                    },
                ],
                
            }
                            

        };
    };

    return {
        traces,
        layout: tableLayout,
    }
}



export const hist_data = (method: SingleClassificationModel) => {
    if (!method.y_true || !method.y_pred || !method.y_prob) {

        return null;

    } else {

        let pred_vals = [];
        let true_vals = [];
        let i = 0;
        for (let val of method.y_true) {
        if (val === 0) {
            pred_vals.push(method.y_prob[i])
        }
        i++;
        }
        let j = 0;
        for (let val of method.y_true) {
        if (val === 1) {
            true_vals.push(method.y_prob[j])
        }
        j++;
        }

        var trace1 = {
        x: pred_vals,
        name: 'Predicted Values',
        autobinx: false, 
        histnorm: "count", 
        marker: {
            color: "rgb(219, 30, 58, .7)", 
            line: {
            color:  "rgb(219, 30, 58, 1)", 
            width: 1
            }
        },  
        opacity: 0.5, 
        type: "histogram", 
        xbins: {
            end: 1, 
            size: 0.025, 
            start: 0
            }
        };

        var trace2 = {
        x: true_vals,
        name: "True Values", 
        histnorm: "count",
        autobinx: false, 
        marker: {
                color: "rgb(94, 146, 219, .7)",
                line: {
                    color:  "rgb(94, 146, 219, 1)", 
                    width: 1
            } 
            }, 
        opacity: 0.5, 
        type: "histogram", 
        xbins: { 
            end: 1, 
            size: 0.025, 
            start: 0
            }
        };

        return [trace1, trace2]

    }
    
}

export const scatterPlotLayout = (data: any[]) => {
    const combinedData = ((data[0] as Plotly.ScatterData).x as number[]).concat((data[0] as Plotly.ScatterData).y as number[]);
    const sortedData = combinedData.sort((a,b) => a - b);

    let low_value = Math.floor(Number(sortedData[0]));
    let high_value = Math.round(Number(sortedData[sortedData.length - 1]));

    return {
        title: {
        text: "Cross-Validation Predictions",
        yref: 'paper',
        xref: 'paper',
        yanchor: 'bottom',
        y: 1,
        pad: {
            b:10,
            l:0,
            r:0,
            t:10
        },
        xanchor: 'auto',
        font: {
            family: 'Montserrat',
            size: 16,
        }
        },
        xaxis: {range: [low_value, high_value]},
        yaxis:  {range: [low_value, high_value]},
        responsive: true,
        autosize: true,
        showlegend:false,
        margin: {
        l: 30,
        r: 10,
        b: 30,
        t: 60,
        pad: 0
        },
        modebar: {
        color: '#555555',
        activecolor: '#315FB5',
        },
        shapes: [
        {
            type: 'line',
            x0: low_value,
            y0: low_value,
            x1: high_value,
            y1: high_value,
            line: {
            color: '#999999',
            width: 2,
            dash: 'dot'
            }
        }
        ]
    }
}

export const rgbColors = {
    bnb: 'rgba(44, 114, 142, 0.1)',
    DL: 'rgba(33, 144, 140, 0.1)',
    ada: '#rgba(59, 82, 139, 0.1)',
    knn: 'rgba(39, 173, 129, 0.1)',
    lreg: 'rgba(93, 200, 99, 0.1)',
    rf: 'rgba(170, 220, 50, 0.1)',
    svc: 'rgba(253, 231, 37, 0.1)',
    xgb: 'rgba(255, 197, 111, 0.1)',
}

export const trColors = {
    bnb: '#2C728E',
    DL: '#21908C',
    ada: '#3B528B',
    knn: '#27AD81',
    lreg: '#5DC863',
    rf: '#AADC32',
    svc: '#FDE725',
    xgb: '#FFC56F'
}

export const regTrColors = {
    r2: '#DB5C68',
    mae: '#B8328A',
    rmse: '#5802A3',
}

export const getAtomOrBondColors = (atomsOrBonds: {[key: number]: number}) => {
    let atoms_or_bonds: number[] = [];
    
    atoms_or_bonds = Object.keys(atomsOrBonds).map(key => Number(key));

    let colors_red: {[key: number]: number} = {};
    let colors_green: {[key: number]: number} = {};
    let colors_blue: {[key: number]: number} = {};

    atoms_or_bonds.forEach(atom_or_bond => {
        (colors_red as any)[atom_or_bond] = -1 * Math.min(0, atomsOrBonds[atom_or_bond]);
        (colors_green as any)[atom_or_bond] = 0;
        (colors_blue as any)[atom_or_bond] = Math.max(0, atomsOrBonds[atom_or_bond])
    });

    let colors: {[key: number]: number[]} = {};

    Object.keys(colors_blue).map(atom_or_bond => Number(atom_or_bond)).forEach(atom_or_bond => {
        (colors as any)[atom_or_bond] = [
            1 - (colors_blue as any)[atom_or_bond] - (colors_green as any)[atom_or_bond],
            1 - (colors_red as any)[atom_or_bond] - (colors_blue as any)[atom_or_bond],
            1 - (colors_red as any)[atom_or_bond] - (colors_green as any)[atom_or_bond],
        ]
    });

    return {
        atoms_or_bonds,
        colors
    }
}